import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { aiConfigService } from "@/api/aiConfig";
import { TaleDashbaordMainCard } from "@components/TaleDashbaordMainCard";
import TaleButton from "@components/TaleButton";
import { TaleInput } from "@components/TaleInput";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { InternalServerError } from "@components/Errors/InternalServerError";
import { useTranslation } from "@/hooks/useTranslation";

const AiConfiguration = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [formData, setFormData] = useState({
        endpoint: "",
        apiKey: "",
        deploymentName: "",
        temperature: 0,
        maxTokens: 0,
    });

    const { data: config, isLoading, error } = useQuery({
        queryKey: ["ai-config"],
        queryFn: aiConfigService.getConfig,
        retry: false,
    });

    const updateConfigMutation = useMutation({
        mutationFn: (data) => aiConfigService.updateConfig(data),
        onSuccess: () => {
            queryClient.invalidateQueries(["ai-config"]);
            alert(t("aiConfig.updateSuccess"));
        },
        onError: (error) => {
            console.error("Update error:", error);
            alert(t("aiConfig.updateError"));
        },
    });

    useEffect(() => {
        if (config) {
            setFormData({
                endpoint: config.endpoint || "",
                apiKey: config.apiKey || "",
                deploymentName: config.deploymentName || "",
                temperature: config.temperature ?? 0,
                maxTokens: config.maxTokens ?? 0,
            });
        }
    }, [config]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = () => {
        if (!formData.endpoint || !formData.apiKey || !formData.deploymentName) {
            alert(t("aiConfig.fillRequiredFields"));
            return;
        }
        updateConfigMutation.mutate({
            ...formData,
            temperature: parseFloat(formData.temperature),
            maxTokens: parseInt(formData.maxTokens),
        });
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <TaleLoadingFallback />
            </div>
        );
    }

    if (error) {
        return <InternalServerError />;
    }

    return (
        <TaleDashbaordMainCard className="p-6">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">{t("aiConfig.title")}</h1>
                <p className="text-gray-600 mt-2">
                    {t("aiConfig.subtitle")}
                </p>
                <hr />
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-xl border">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-blue-600 font-medium">{t("aiConfig.deploymentLabel")}</div>
                            <div className="text-xl font-bold text-gray-900 truncate">
                                {config?.deploymentName || "—"}
                            </div>
                        </div>
                        <i className="fas fa-rocket text-3xl text-blue-400"></i>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">{t("aiConfig.deploymentDesc")}</div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-green-600 font-medium">{t("aiConfig.temperatureLabel")}</div>
                            <div className="text-2xl font-bold text-gray-900">
                                {config?.temperature ?? "—"}
                            </div>
                        </div>
                        <i className="fas fa-thermometer-half text-3xl text-green-400"></i>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">{t("aiConfig.temperatureDesc")}</div>
                </div>

                <div className="bg-gray-50 p-6 rounded-xl border">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm text-purple-600 font-medium">{t("aiConfig.maxTokensLabel")}</div>
                            <div className="text-2xl font-bold text-gray-900">
                                {config?.maxTokens ?? "—"}
                            </div>
                        </div>
                        <i className="fas fa-align-left text-3xl text-purple-400"></i>
                    </div>
                    <div className="mt-3 text-sm text-gray-600">{t("aiConfig.maxTokensDesc")}</div>
                </div>
            </div>

            {/* Form Section */}
            <div className="space-y-8">
                <div className="border-t pt-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4">
                            <i className="fas fa-sliders-h text-blue-500 text-xl"></i>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{t("aiConfig.modelParamsTitle")}</h2>
                            <p className="text-gray-600 text-sm">
                                {t("aiConfig.modelParamsDesc")}
                            </p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <TaleInput
                                label={t("aiConfig.endpointLabel")}
                                name="endpoint"
                                value={formData.endpoint}
                                onChange={handleInputChange}
                                placeholder="https://your-resource.openai.azure.com/"
                            />
                            <TaleInput
                                label={t("aiConfig.deploymentNameLabel")}
                                name="deploymentName"
                                value={formData.deploymentName}
                                onChange={handleInputChange}
                                placeholder={t("aiConfig.deploymentNamePlaceholder")}
                            />
                        </div>

                        <TaleInput
                            label={t("aiConfig.apiKeyLabel")}
                            name="apiKey"
                            value={formData.apiKey}
                            onChange={handleInputChange}
                            placeholder={t("aiConfig.apiKeyPlaceholder")}
                            type="password"
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <TaleInput
                                label={t("aiConfig.temperatureLabel")}
                                name="temperature"
                                type="number"
                                step="0.1"
                                min="0"
                                max="2"
                                value={formData.temperature}
                                onChange={handleInputChange}
                                placeholder="Ex: 0.7"
                                helperText={t("aiConfig.temperatureHelper")}
                            />
                            <TaleInput
                                label={t("aiConfig.maxTokensLabel")}
                                name="maxTokens"
                                type="number"
                                min="1"
                                value={formData.maxTokens}
                                onChange={handleInputChange}
                                placeholder="Ex: 2048"
                                helperText={t("aiConfig.maxTokensHelper")}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end mt-8">
                        <TaleButton
                            onClick={handleSubmit}
                            loading={updateConfigMutation.isPending}
                        >
                            {t("aiConfig.saveConfigButton")}
                        </TaleButton>
                    </div>
                </div>

                {/* Info Section */}
                <div className="border-t pt-8">
                    <div className="flex items-center mb-6">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center mr-4 rtl:mr-0 rtl:ml-4">
                            <i className="fas fa-question-circle text-gray-500 text-xl"></i>
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{t("aiConfig.guideTitle")}</h2>
                            <p className="text-gray-600 text-sm">{t("aiConfig.guideDesc")}</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-6 bg-gray-50 rounded-xl border">
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                                    <i className="fas fa-link text-blue-500"></i>
                                </div>
                                <div className="font-semibold text-blue-700">{t("aiConfig.endpointGuideTitle")}</div>
                            </div>
                            <p className="text-sm text-gray-600">
                                {t("aiConfig.endpointGuideDesc")}
                            </p>
                        </div>

                        <div className="p-6 bg-gray-50 rounded-xl border">
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                                    <i className="fas fa-thermometer-half text-green-500"></i>
                                </div>
                                <div className="font-semibold text-green-700">{t("aiConfig.temperatureLabel")}</div>
                            </div>
                            <p className="text-sm text-gray-600">
                                {t("aiConfig.temperatureGuideDesc")}
                            </p>
                        </div>

                        <div className="p-6 bg-gray-50 rounded-xl border">
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                                    <i className="fas fa-align-left text-purple-500"></i>
                                </div>
                                <div className="font-semibold text-purple-700">{t("aiConfig.maxTokensLabel")}</div>
                            </div>
                            <p className="text-sm text-gray-600">
                                {t("aiConfig.maxTokensGuideDesc")}
                            </p>
                        </div>

                        <div className="p-6 bg-gray-50 rounded-xl border">
                            <div className="flex items-center mb-3">
                                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                                    <i className="fas fa-rocket text-orange-500"></i>
                                </div>
                                <div className="font-semibold text-orange-700">{t("aiConfig.deploymentNameGuideTitle")}</div>
                            </div>
                            <p className="text-sm text-gray-600">
                                {t("aiConfig.deploymentGuideDesc")}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </TaleDashbaordMainCard>
    );
};

export default AiConfiguration;